import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  Typography,
  Button,
  Tooltip,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Collapse,
  InputBase,
  styled,
} from "@mui/material";
import {
  MoreHoriz as MoreHorizIcon,
  EditOutlined as EditOutlinedIcon,
  ShareOutlined as ShareOutlinedIcon,
  DeleteOutline as DeleteOutlineIcon,
  FolderOutlined as FolderOutlinedIcon,
  FolderSharedOutlined as FolderSharedOutlinedIcon,
  CreateNewFolderOutlined as CreateNewFolderOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
} from "@mui/icons-material";

// Types
export type SidebarProject = {
  id: number;
  name: string;
  is_shared: boolean;
  owner_user_id?: string;
  icon?: React.ReactNode;
};

export type MeGenSidebarMUIProps = {
  projects?: SidebarProject[];
  activeProjectId?: number;
  ownerNameMap?: Record<string, string>;
  onNewProject?: () => void;
  onSelectProject?: (projectId: number) => void;
  onEditProject?: (projectId: number, projectName: string) => void;
  onShareProject?: (projectId: number, projectName: string) => void;
  onDeleteProject?: (projectId: number, projectName: string) => void;
  defaultCollapsed?: boolean;
  collapsed?: boolean;
  onCollapseChange?: (isCollapsed: boolean) => void;
};

// Styled Components
const SIDEBAR_WIDTH = 200;

const SidebarWrapper = styled(Box)({
  position: "relative",
  height: "100%",
  display: "flex",
  flexShrink: 0,
});

const SIDEBAR_COLLAPSED_WIDTH = 44;

const SidebarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isCollapsed",
})<{ isCollapsed?: boolean }>(({ isCollapsed }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  borderRight: "1px solid #E8E8E8",
  transition: "width 0.3s ease",
  overflow: "hidden",
  width: isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
}));

const SidebarHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1),
}));

const ExpandButton = styled(IconButton)({
  position: "absolute",
  left: 8,
  top: 8,
  backgroundColor: "white",
  zIndex: 1005,
  borderRadius: 6,
  padding: 8,
  "&:hover": {
    backgroundColor: "whitesmoke",
  },
});

const NewProjectButton = styled(Button)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing(0.5),
  padding: "10px 14px",
  margin: "8px 0px",
  backgroundColor: "transparent",
  color: "#333",
  fontSize: "14px",
  fontWeight: 500,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#EEEEEE",
  },
}));

const SectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 12px",
  cursor: "pointer",
  userSelect: "none",
  "&:hover": {
    backgroundColor: "whitesmoke",
    borderRadius: 4,
  },
});

const SectionLabel = styled(Typography)({
  fontSize: "12px",
  fontWeight: 600,
  color: "#888",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  flexShrink: 0,
});

const SectionBadge = styled(Box)({
  fontSize: "11px",
  fontWeight: 500,
  color: "#AAA",
  backgroundColor: "#F0F0F0",
  borderRadius: "10px",
  padding: "1px 7px",
  marginLeft: "6px",
  lineHeight: "18px",
});

const SectionTitle = styled(Typography)({
  padding: "8px 12px",
  fontSize: "12px",
  fontWeight: 600,
  color: "#888",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const ProjectList = styled(List)({
  // flex: 1,
  overflowY: "auto",
});

const ProjectListItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ isActive }) => ({
  padding: "8px 12px",
  backgroundColor: isActive ? "#FFE4E8" : "transparent",
  color: isActive ? "#E91E63" : "#333",
  "&:hover": {
    backgroundColor: isActive ? "#FFE4E8" : "#F0F0F0",
  },
  "& .more-button": {
    opacity: 0,
    transition: "opacity 0.2s ease",
  },
  "&:hover .more-button": {
    opacity: 1,
  },
}));

const MoreButton = styled(IconButton)({
  padding: 4,
  marginLeft: "auto",
  color: "#94A3B8",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    color: "#64748B",
  },
});

const StyledMenu = styled(Menu)({
  "& .MuiPaper-root": {
    borderRadius: "0.5rem",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
    border: "1px solid #e2e8f0",
    minWidth: "140px",
  },
});

const StyledMenuItem = styled(MenuItem)({
  padding: "8px 12px",
  fontSize: "0.875rem",
  gap: "8px",
  "&:hover": {
    backgroundColor: "#f8fafc",
  },
});

const DeleteMenuItem = styled(MenuItem)({
  padding: "8px 12px",
  fontSize: "0.875rem",
  gap: "8px",
  color: "#ef4444",
  "&:hover": {
    backgroundColor: "#fef2f2",
  },
});

const SearchWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  margin: "0 8px 8px 8px",
  padding: "4px 10px",
  borderRadius: "8px",
  backgroundColor: "whitesmoke",
  border: "1px solid transparent",
  "&:focus-within": {
    border: "1px solid #E91E63",
    backgroundColor: "white",
  },
});

const SearchInput = styled(InputBase)({
  flex: 1,
  fontSize: "13px",
  color: "#333",
  "& input::placeholder": {
    color: "#AAA",
    fontSize: "13px",
  },
});

const MeGenSidebarMUI: React.FC<MeGenSidebarMUIProps> = (props) => {
  // Sidebar States
  const [isCollapsed, setIsCollapsed] = useState(props.defaultCollapsed || false);

  // Sync with controlled `collapsed` prop
  const isExternalUpdate = useRef(false);
  useEffect(() => {
    if (props.collapsed !== undefined && props.collapsed !== isCollapsed) {
      isExternalUpdate.current = true;
      setIsCollapsed(props.collapsed);
    }
  }, [props.collapsed]);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMenuProject, setSelectedMenuProject] = useState<SidebarProject | null>(null);

  // Expand / Collapse States
  const [myProjectsExpanded, setMyProjectsExpanded] = useState<boolean>(true);
  const [sharedProjectsExpanded, setSharedProjectsExpanded] = useState<boolean>(true);

  // Search State
  const [searchQuery, setSearchQuery] = useState<string>("");

  const menuOpen = Boolean(menuAnchorEl);

  useEffect(() => {
    if (isExternalUpdate.current) {
      isExternalUpdate.current = false;
      return;
    }
    props.onCollapseChange?.(isCollapsed);
  }, [isCollapsed, props.onCollapseChange]);

  const handleCollapse = () => {
    setIsCollapsed(true);
  };

  const handleExpand = () => {
    setIsCollapsed(false);
  };

  const handleMoreClick = (
    e: React.MouseEvent<HTMLElement>,
    project: SidebarProject
  ) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setSelectedMenuProject(project);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedMenuProject(null);
  };

  const handleEditClick = () => {
    if (selectedMenuProject) {
      props.onEditProject?.(selectedMenuProject.id, selectedMenuProject.name);
    }
    handleMenuClose();
  };

  const handleShareClick = () => {
    if (selectedMenuProject) {
      props.onShareProject?.(selectedMenuProject.id, selectedMenuProject.name);
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    if (selectedMenuProject) {
      props.onDeleteProject?.(selectedMenuProject.id, selectedMenuProject.name);
    }
    handleMenuClose();
  };

  const filteredProjects = (props.projects || []).filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const myProjects = filteredProjects.filter(p => !p.is_shared);
  const sharedProjects = filteredProjects.filter(p => p.is_shared);

  // Helper
  const getOwnerLabel = (project: SidebarProject): string | undefined => {
    if (!project.is_shared || !project.owner_user_id) return undefined;

    const name = props.ownerNameMap?.[project.owner_user_id];
    
    return name ? `Shared by ${name}` : "Shared Project";
  };

  // * ========= Header Section ========== *//
  const renderSectionHeader = (
    label: string,
    count: number,
    expanded: boolean,
    onToggle: () => void,
    headerId?: string,
  ) => (
    <SectionHeader id={headerId} onClick={onToggle}>
      <IconButton size="small" sx={{ padding: 0, color: "#AAA" }}>
        {expanded
          ? <ExpandMoreIcon sx={{ fontSize: 16 }} />
          : <ChevronRightIcon sx={{ fontSize: 16 }} />
        }
      </IconButton>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <SectionLabel>{label}</SectionLabel>
        <SectionBadge>{count}</SectionBadge>
      </Box>
    </SectionHeader>
  );

  return (
    <SidebarWrapper id="megen-sidebar-wrapper">
      {/* Sidebar Content */}
      <SidebarContainer id="megen-sidebar-container" isCollapsed={isCollapsed}>
        {/* Collapsed icon strip */}
        {isCollapsed ? (
          <Box id="megen-sidebar-collapsed-strip" sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: 1, gap: 0.5 }}>
            {/* Expand */}
            <Tooltip title="Open sidebar" placement="right">
              <IconButton id="megen-sidebar-collapsed-expand-btn" onClick={handleExpand} size="small" sx={{ borderRadius: 1, "&:hover": { backgroundColor: "#EEEEEE" } }}>
                <ChevronRightIcon sx={{ color: "#666", fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            {/* New Project icon */}
            <Tooltip title="New project" placement="right">
              <IconButton id="megen-sidebar-collapsed-new-project-btn" onClick={props.onNewProject} size="small" sx={{ borderRadius: 1, "&:hover": { backgroundColor: "#EEEEEE" } }}>
                <CreateNewFolderOutlinedIcon sx={{ color: "#555", fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            {/* Shared Projects section */}
            {sharedProjects.length > 0 && (
              <>
                <Typography id="megen-sidebar-collapsed-shared-label" sx={{
                  fontSize: "8px", fontWeight: 700, color: "#BBB",
                  textTransform: "uppercase", letterSpacing: "0.5px",
                  lineHeight: 1,
                  userSelect: "none", mt: 0.5, mb: 0.5,
                }}>
                  SHR
                </Typography>
                {sharedProjects.map((project) => (
                  <Tooltip key={project.id} title={project.name} placement="right">
                    <IconButton
                      id={`megen-sidebar-collapsed-shared-item-${project.id}`}
                      onClick={() => props.onSelectProject?.(project.id)}
                      size="small"
                      sx={{
                        borderRadius: 1, width: 32, height: 32,
                        backgroundColor: props.activeProjectId === project.id ? "#FFE4E8" : "transparent",
                        color: props.activeProjectId === project.id ? "#E91E63" : "#666",
                        "&:hover": { backgroundColor: props.activeProjectId === project.id ? "#FFE4E8" : "#EEEEEE" },
                      }}
                    >
                      <Typography sx={{ fontSize: "12px", fontWeight: 600, lineHeight: 1 }}>
                        {project.name.charAt(0).toUpperCase()}
                      </Typography>
                    </IconButton>
                  </Tooltip>
                ))}
              </>
            )}

            {/* My Projects section */}
            {myProjects.length > 0 && (
              <>
                <Typography id="megen-sidebar-collapsed-my-label" sx={{
                  fontSize: "8px", fontWeight: 700, color: "#BBB",
                  textTransform: "uppercase", letterSpacing: "0.5px",
                  lineHeight: 1,
                  userSelect: "none", mt: 0.5, mb: 0.5,
                }}>
                  My
                </Typography>
                {myProjects.map((project) => (
                  <Tooltip key={project.id} title={project.name} placement="right">
                    <IconButton
                      id={`megen-sidebar-collapsed-my-item-${project.id}`}
                      onClick={() => props.onSelectProject?.(project.id)}
                      size="small"
                      sx={{
                        borderRadius: 1, width: 32, height: 32,
                        backgroundColor: props.activeProjectId === project.id ? "#FFE4E8" : "transparent",
                        color: props.activeProjectId === project.id ? "#E91E63" : "#666",
                        "&:hover": { backgroundColor: props.activeProjectId === project.id ? "#FFE4E8" : "#EEEEEE" },
                      }}
                    >
                      <Typography sx={{ fontSize: "12px", fontWeight: 600, lineHeight: 1 }}>
                        {project.name.charAt(0).toUpperCase()}
                      </Typography>
                    </IconButton>
                  </Tooltip>
                ))}
              </>
            )}
          </Box>
        ) : (
          <>
        {/* Header with Collapse Button */}
        <SidebarHeader id="megen-sidebar-header">
          <Box />
          <Tooltip title="Close sidebar" placement="right">
            <IconButton
              id="megen-sidebar-collapse-btn"
              onClick={handleCollapse}
              size="small"
              sx={{
                borderRadius: 1,
                "&:hover": {
                  backgroundColor: "#EEEEEE",
                },
              }}
            >
              <ChevronLeftIcon sx={{ color: "#666", fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </SidebarHeader>

        {/* New Project Button */}
        <NewProjectButton
          id="megen-sidebar-new-project-btn"
          fullWidth
          startIcon={<CreateNewFolderOutlinedIcon />}
          onClick={props.onNewProject}
        >
          New project
        </NewProjectButton>

        {/* Search Bar */}
        <SearchWrapper id="megen-sidebar-search-wrapper">
          <SearchIcon sx={{ fontSize: 16, color: "#AAA", mr: 0.5 }} />
          <SearchInput
            id="megen-sidebar-search-input"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <IconButton
              id="megen-sidebar-search-clear-btn"
              size="small"
              onClick={() => setSearchQuery("")}
              sx={{ padding: 0, color: "#AAA" }}
            >
              <ClearIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </SearchWrapper>

        <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
          {/* Shared Projects List */}
          {sharedProjects.length > 0 && (
            <>
              {renderSectionHeader(
                "Shared Projects",
                sharedProjects.length,
                sharedProjectsExpanded,
                () => setSharedProjectsExpanded(prev => !prev),
                "megen-sidebar-section-header-shared-projects"
              )}
              <Collapse id="megen-sidebar-shared-section" in={sharedProjectsExpanded} timeout="auto">
                <ProjectList id="megen-sidebar-shared-list" disablePadding>
                  {sharedProjects.map((project) => (
                    <ProjectListItem
                      id={`megen-sidebar-shared-project-item-${project.id}`}
                      key={project.id}
                      isActive={props.activeProjectId === project.id}
                      onClick={() => props.onSelectProject?.(project.id)}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 32,
                          color: props.activeProjectId === project.id ? "#E91E63" : "#666"
                        }}
                      >
                        <FolderSharedOutlinedIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={project.name}
                        secondary={getOwnerLabel(project)}
                        primaryTypographyProps={{
                          sx: {
                            fontSize: "14px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          },
                        }}
                        secondaryTypographyProps={{
                          sx: {
                            fontSize: "10px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          },
                        }}
                      />
                    </ProjectListItem>
                  ))}
                </ProjectList>
              </Collapse>
            </>
          )}

          {/* Project List */}
          {renderSectionHeader(
            "My Projects",
            myProjects.length,
            myProjectsExpanded,
            () => setMyProjectsExpanded(prev => !prev),
            "megen-sidebar-section-header-my-projects"
          )}
          <Collapse id="megen-sidebar-my-section" in={myProjectsExpanded} timeout="auto">
            <ProjectList id="megen-sidebar-my-list" disablePadding>
              {myProjects.map((project) => (
                <ProjectListItem
                  id={`megen-sidebar-my-project-item-${project.id}`}
                  key={project.id}
                  isActive={props.activeProjectId === project.id}
                  onClick={() => props.onSelectProject?.(project.id)}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 32,
                      color: props.activeProjectId === project.id ? "#E91E63" : "#666",
                    }}
                  >
                    {project.icon || <FolderOutlinedIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={project.name}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: "14px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                  {/* More Button (...) */}
                  <Tooltip title="More options" placement="right">
                    <MoreButton
                      id={`megen-sidebar-project-more-btn-${project.id}`}
                      className="more-button"
                      size="small"
                      onClick={(e) => handleMoreClick(e, project)}
                    >
                      <MoreHorizIcon sx={{ fontSize: 18 }} />
                    </MoreButton>
                  </Tooltip>
                </ProjectListItem>
              ))}
            </ProjectList>
          </Collapse>
        </Box>

        {/* Dropdown Menu */}
        <StyledMenu
          id="megen-sidebar-context-menu"
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <StyledMenuItem id="megen-sidebar-menu-edit" onClick={handleEditClick}>
            <EditOutlinedIcon sx={{ fontSize: 18, color: "#64748b" }} />
            <Typography sx={{ fontSize: "0.875rem" }}>Edit</Typography>
          </StyledMenuItem>
          <StyledMenuItem id="megen-sidebar-menu-share" onClick={handleShareClick}>
            <ShareOutlinedIcon sx={{ fontSize: 18, color: "#64748b" }} />
            <Typography sx={{ fontSize: "0.875rem" }}>Share</Typography>
          </StyledMenuItem>
          <DeleteMenuItem id="megen-sidebar-menu-delete" onClick={handleDeleteClick}>
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: "0.875rem" }}>Delete</Typography>
          </DeleteMenuItem>
        </StyledMenu>
          </>
        )}
      </SidebarContainer>
    </SidebarWrapper>
  );
};

export default MeGenSidebarMUI;